import { createColumnHelper } from "@tanstack/table-core";
import { useMemo } from "react";
import { Alert, Col, Row } from "react-bootstrap";
import type { ActivityLog } from "../../../api/src/activity-log/lib";
import Loading from "../Loading";
import { Table, useTable } from "./Table/Table";

type ActivityLogRow = ActivityLog;

const ActivityLogTable = ({
  isLoading,
  isError,
  activities,
}: {
  isLoading: boolean;
  isError: boolean;
  activities: ActivityLog[];
}) => {
  const columnHelper = useMemo(() => createColumnHelper<ActivityLogRow>(), []);

  const columns = useMemo(
    () => [
      columnHelper.accessor("createdAt", {
        cell: (info) => {
          const date = info.getValue();
          return date.toLocaleString();
        },
        header: () => "Date",
        enableColumnFilter: false,
      }),
      columnHelper.accessor("username", {
        cell: (info) => info.getValue() ?? "System",
        header: () => "User",
      }),
      columnHelper.accessor("action", {
        cell: (info) => {
          const value = info.getValue();
          return value.charAt(0).toUpperCase() + value.slice(1);
        },
        header: () => "Action",
      }),
      columnHelper.accessor("description", {
        cell: (info) => info.getValue(),
        header: () => "Description",
        enableColumnFilter: false,
      }),
    ],
    [columnHelper],
  );

  const { table } = useTable({
    columns,
    data: activities,
    initialState: {
      sorting: [{ id: "createdAt", desc: true }],
    },
    getRowId: (row) => row.id.toString(),
    autoResetAll: false,
  });

  return isLoading ? (
    <Loading message="Activity Log" />
  ) : isError ? (
    <Alert variant="danger">An error occurred loading the activity log</Alert>
  ) : activities.length === 0 ? (
    <Alert variant="info">No activity recorded yet</Alert>
  ) : (
    <Row>
      <Col>
        <Table table={table} />
      </Col>
    </Row>
  );
};

export default ActivityLogTable;
